import { getServiceMpvId } from '@99designs/design-services-common';

export interface OverflowServiceConfig {
    esMpvId: string;
    designerGroupOption?: { key: string; value: string };
}

/**
 * Supported locales for overflow service configuration.
 */
const SUPPORTED_OVERFLOW_LOCALES = ['en-US', 'es-US', 'en-CA', 'fr-CA', 'en-AU', 'en-SG'];

/**
 * Determines overflow service configuration for the Header component.
 * This helper encapsulates the logic for mapping print MPV IDs to service MPV IDs.
 * Only applies the overflow mapping for supported locales.
 *
 * @param routeSpecifiedMpvId - The MPV ID from the route
 * @param locale - The current locale
 * @returns OverflowServiceConfig with esMpvId (without designer group option), or null if not supported
 */
export function getOverflowServiceConfig(
    routeSpecifiedMpvId: string,
    locale: string,
    reviewMethod: string
): OverflowServiceConfig | null {
    // Only apply overflow mapping for supported locales
    if (!SUPPORTED_OVERFLOW_LOCALES.includes(locale)) {
        return null;
    }

    if (reviewMethod !== 'Standard') {
        return null;
    }

    // Get the service MPV ID from the shared mapping
    const serviceMpvId = getServiceMpvId(routeSpecifiedMpvId);

    if (!serviceMpvId) {
        return null;
    }

    return {
        esMpvId: serviceMpvId,
    };
}

/**
 * Determines if designer group option should be added based on overflow config.
 * Rule: If we have an overflow config AND the esMpvId is not 'caredesign' (case insensitive),
 * then we need DesignerGroupOption.
 *
 * @param overflowConfig - The overflow config (if any)
 * @returns OverflowServiceConfig with designer group option added if needed
 */
export function addDesignerGroupOptionIfNeeded(
    overflowConfig: OverflowServiceConfig | null
): OverflowServiceConfig | null {
    if (!overflowConfig) {
        return null;
    }

    const configWithOption = { ...overflowConfig };

    // Add designer group option if esMpvId !== caredesign (case insensitive)
    if (overflowConfig.esMpvId.toLowerCase() !== 'caredesign') {
        configWithOption.designerGroupOption = {
            key: 'DesignerGroupOption',
            value: 'In-house',
        };
    }

    return configWithOption;
}
